import LmsTitleDescriptionFields from '../../components/LmsTitleDescriptionFields';
import LmsEditorHeader from '../../components/LmsEditorHeader';
import FormActionButtons from '../../components/FormActionButtons';
import { PageLoader } from '../../components/PageLoader';
import { Spinner } from '../../components/loading';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Calendar,
  Flag,
  Trophy,
  AlertTriangle,
  UserPlus,
  Power,
  PowerOff,
} from 'lucide-react';
import { lmsAPI, type LmsStagePayload } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import LmsAudiencePicker from '../../components/LmsAudiencePicker';
import {
  applyLmsAssignmentDiff,
  diffLmsAssignments,
  splitLmsAssignments,
  type ExistingLmsAssignment,
} from '../../utils/lmsAssignments';

type UnlockCondition = 'sequential' | 'score_based';

interface StageContent {
  /** Server id for saved rows; a `temp-...` placeholder until first save. */
  id: string;
  content_type: 'module' | 'session';
  module_id?: string;
  session_id?: string;
  title: string;
  order_index: number;
  is_required: boolean;
  /** False for a draft module or a cancelled session — attached, but invisible to learners. */
  is_available?: boolean;
}

interface JourneyStage {
  id: string;
  title: string;
  description: string;
  order_index: number;
  unlock_condition: UnlockCondition;
  required_score: number;
  is_milestone: boolean;
  milestone_name: string;
  milestone_points_bonus: number;
  /** "Finish this stage within N days"; 0 = no stage due date. */
  due_within_days: number;
  content: StageContent[];
}

interface JourneyForm {
  title: string;
  description: string;
  thumbnail_url: string;
  estimated_hours: number;
  is_active: boolean;
  /** 'everyone' = the whole project; 'assigned' = only its assignees. */
  audience: 'everyone' | 'assigned';
  /** "Complete within N days"; 0 = no due date. */
  due_within_days: number;
}

interface PickerItem {
  id: string;
  title: string;
  subtitle?: string;
}

const isTempId = (id: string) => id.startsWith('temp-');

/** "Within N days" fields hold whole days, a year at most. Blank, or anything that is not a number, is 0 = no due date. */
const DUE_DAYS_MAX = 365;
const toDueDays = (value: unknown) => Math.min(DUE_DAYS_MAX, Math.max(0, parseInt(String(value ?? '')) || 0));
const daysLabel = (days: number) => `${days} ${days === 1 ? 'day' : 'days'}`;

/** Monotonic ids for unsaved rows — Date.now() alone collides when two rows are added in the same millisecond. */
let tempIdCounter = 0;
const nextTempId = () => `temp-${Date.now()}-${tempIdCounter++}`;

export default function LmsJourneyBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const projectId = useAuthStore((state) => state.selectedProjectId) ?? undefined;
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [journeyForm, setJourneyForm] = useState<JourneyForm>({
    title: '',
    description: '',
    thumbnail_url: '',
    estimated_hours: 0,
    is_active: true,
    audience: 'everyone',
    due_within_days: 0,
  });
  // What the server currently holds, as opposed to the unsaved form value. The
  // header badge and the activate button describe the saved journey.
  const [savedActive, setSavedActive] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);
  const [stages, setStages] = useState<JourneyStage[]>([]);
  const [expandedStage, setExpandedStage] = useState<string | null>(null);

  // Audience, picked inline. Journey assignments live in the same
  // lms_module_assignments table as module and session ones, keyed by
  // journey_id, and are saved as a diff after the journey itself.
  const [assignedRoleIds, setAssignedRoleIds] = useState<string[]>([]);
  const [assignedEmployeeCodes, setAssignedEmployeeCodes] = useState<string[]>([]);
  const [existingAssignments, setExistingAssignments] = useState<ExistingLmsAssignment[]>([]);

  // Content that can be attached to a stage.
  const [availableModules, setAvailableModules] = useState<PickerItem[]>([]);
  const [availableSessions, setAvailableSessions] = useState<PickerItem[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  // Load the journey being edited.
  const fetchJourney = useCallback(async () => {
    if (!isEditing || !id) {
      setLoading(false);
      return;
    }
    // Loading an existing journey needs a project. Falling through would render
    // an empty form over a journey that actually has stages.
    if (!projectId) {
      setLoadError('Select a project to edit this journey');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await lmsAPI.getJourney(id, projectId);
      const data = response.data?.data;
      if (!data?.journey) {
        setLoadError('Journey not found');
        return;
      }

      setJourneyForm({
        title: data.journey.title ?? '',
        description: data.journey.description ?? '',
        thumbnail_url: data.journey.thumbnail_url ?? '',
        estimated_hours: Number(data.journey.estimated_hours) || 0,
        is_active: data.journey.is_active !== false,
        audience: data.journey.audience === 'assigned' ? 'assigned' : 'everyone',
        due_within_days: toDueDays(data.journey.due_within_days),
      });
      setSavedActive(data.journey.is_active === true);

      // Seed the inline picker from what the journey already has, so a save
      // diffs against it rather than re-adding every row.
      try {
        const assignmentsResponse = await lmsAPI.getJourneyAssignments(id, projectId);
        const existing: ExistingLmsAssignment[] = assignmentsResponse.data?.data || [];
        setExistingAssignments(existing);
        const seeded = splitLmsAssignments(existing);
        setAssignedRoleIds(seeded.roleIds);
        setAssignedEmployeeCodes(seeded.employeeCodes);
      } catch (assignmentErr) {
        console.error('Error loading journey assignments:', assignmentErr);
      }

      const loadedStages: JourneyStage[] = (data.stages || []).map((stage: any, index: number) => ({
        id: stage.id,
        title: stage.title ?? `Stage ${index + 1}`,
        description: stage.description ?? '',
        order_index: index,
        unlock_condition: stage.unlock_condition === 'score_based' ? 'score_based' : 'sequential',
        required_score: stage.required_score ?? 70,
        is_milestone: stage.is_milestone === true,
        milestone_name: stage.milestone_name ?? '',
        milestone_points_bonus: stage.milestone_points_bonus ?? 0,
        due_within_days: toDueDays(stage.due_within_days),
        content: (stage.content || []).map((item: any, contentIndex: number) => ({
          id: item.id,
          content_type: item.content_type,
          module_id: item.module_id ?? undefined,
          session_id: item.session_id ?? undefined,
          title: item.title ?? '',
          order_index: contentIndex,
          is_required: item.is_required !== false,
          is_available: item.is_available !== false,
        })),
      }));

      setStages(loadedStages);
      setExpandedStage(loadedStages.length > 0 ? loadedStages[0].id : null);
      setLoadError(null);
    } catch (err) {
      console.error('Error loading journey:', err);
      setLoadError('Failed to load this journey');
    } finally {
      setLoading(false);
    }
  }, [id, isEditing, projectId]);

  useEffect(() => {
    fetchJourney();
  }, [fetchJourney]);

  // Load the modules and sessions an admin can attach. Without this the stage
  // content dropdowns were permanently empty, which is why a stage could never
  // actually be filled in.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    const loadCatalog = async () => {
      setCatalogLoading(true);
      try {
        const [moduleResponse, sessionResponse] = await Promise.all([
          lmsAPI.getContentModules({ status: 'published', availableInJourney: true, projectId }),
          lmsAPI.getSessions({ availableInJourney: true, projectId }),
        ]);
        if (cancelled) return;

        setAvailableModules(
          (moduleResponse.data?.data || []).map((m: any) => ({
            id: m.id,
            title: m.title,
            subtitle: m.module_type === 'scorm' ? 'SCORM' : undefined,
          })),
        );
        setAvailableSessions(
          (sessionResponse.data?.data || [])
            .filter((s: any) => s.status !== 'cancelled')
            .map((s: any) => {
              const scheduled = s.scheduled_at ? new Date(s.scheduled_at) : null;
              // A session that has already run can still be attached (its
              // attendance may already be marked), but flag it: learners who
              // have not attended it can never complete that stage item.
              const isPast = scheduled ? scheduled.getTime() < Date.now() : false;
              return {
                id: s.id,
                title: s.title,
                subtitle: scheduled
                  ? `${scheduled.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}${isPast ? ' · past' : ''}`
                  : undefined,
              };
            }),
        );
      } catch (err) {
        console.error('Error loading journey content catalog:', err);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    };

    loadCatalog();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  /**
   * Write the picked audience against a journey that now exists. Reported
   * separately from the journey save: the journey itself is already stored by
   * this point, and a generic "failed to save journey" would send the admin
   * back to build the whole thing again.
   */
  const saveAssignments = async (journeyId: string) => {
    const diff = diffLmsAssignments(existingAssignments, assignedRoleIds, assignedEmployeeCodes);
    if (diff.toCreate.length === 0 && diff.toDeleteIds.length === 0) return;
    try {
      await applyLmsAssignmentDiff(diff, {
        createAssignments: (inputs) =>
          lmsAPI.createJourneyAssignments(journeyId, inputs, projectId),
        deleteAssignment: (assignmentId) => lmsAPI.deleteAssignment(assignmentId, projectId),
      });
    } catch (assignmentErr) {
      console.error('Error saving journey assignments:', assignmentErr);
      alert(
        'The journey was saved, but its assigned roles and employees could not be updated. ' +
          'Open "Assign Learners" from the journeys list to set them.'
      );
    }
  };

  const handleSave = async () => {
    if (!projectId) {
      alert('Select a project before saving');
      return;
    }
    if (!journeyForm.title.trim()) {
      alert('Journey title is required');
      return;
    }
    if (stages.length === 0) {
      alert('Add at least one stage to the journey');
      return;
    }

    // A content row whose dropdown was never filled in would be silently
    // dropped by the API. Point at it instead of saving a journey that is
    // quietly missing content.
    const emptyRow = stages.find((stage) =>
      stage.content.some((c) => !(c.content_type === 'module' ? c.module_id : c.session_id)),
    );
    if (emptyRow) {
      alert(`"${emptyRow.title}" has a content row with nothing selected. Pick a module/session or remove the row.`);
      setExpandedStage(emptyRow.id);
      return;
    }

    const emptyStage = stages.find((stage) => stage.content.length === 0);
    if (
      emptyStage &&
      !confirm(
        `"${emptyStage.title}" has no content. Learners will pass straight through it. Save anyway?`,
      )
    ) {
      setExpandedStage(emptyStage.id);
      return;
    }

    const payloadStages: LmsStagePayload[] = stages.map((stage, index) => ({
      // Placeholder ids are dropped so the server inserts rather than trying to
      // match an id it has never seen.
      id: isTempId(stage.id) ? undefined : stage.id,
      title: stage.title.trim() || `Stage ${index + 1}`,
      description: stage.description.trim() || null,
      order_index: index,
      unlock_condition: stage.unlock_condition,
      required_score: stage.unlock_condition === 'score_based' ? stage.required_score : null,
      is_milestone: stage.is_milestone,
      milestone_name: stage.is_milestone ? stage.milestone_name.trim() || null : null,
      milestone_points_bonus: stage.is_milestone ? stage.milestone_points_bonus : 0,
      // Always sent, 0 for blank: leaving it out would keep the saved rule.
      due_within_days: stage.due_within_days,
      content: stage.content.map((item, contentIndex) => ({
        content_type: item.content_type,
        module_id: item.content_type === 'module' ? item.module_id : null,
        session_id: item.content_type === 'session' ? item.session_id : null,
        order_index: contentIndex,
        is_required: item.is_required,
      })),
    }));

    const payload = {
      title: journeyForm.title.trim(),
      description: journeyForm.description.trim() || null,
      thumbnail_url: journeyForm.thumbnail_url.trim() || null,
      estimated_hours: journeyForm.estimated_hours,
      is_active: journeyForm.is_active,
      audience: journeyForm.audience,
      due_within_days: journeyForm.due_within_days,
      stages: payloadStages,
    };

    try {
      setSaving(true);
      if (isEditing && id) {
        const response = await lmsAPI.updateJourney(id, payload, projectId);
        await saveAssignments(id);
        // Re-read so temp ids are replaced with the real ones; saving twice in
        // a row would otherwise recreate every stage and drop learner progress.
        await fetchJourney();
        if (response.data?.warning) alert(response.data.warning);
      } else {
        const response = await lmsAPI.createJourney(payload, projectId);
        if (response.data?.warning) alert(response.data.warning);
        const newId = response.data?.data?.id;
        if (newId) {
          await saveAssignments(newId);
          navigate(`/lms/journeys/${newId}`, { replace: true });
          return;
        }
        navigate('/lms/journeys');
        return;
      }
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to save journey';
      console.error('Error saving journey:', err);
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  // The reference builder exposes activation as an explicit action rather than
  // only a form checkbox, because activation is validated server-side (a
  // journey with no content is refused) and the admin needs that answer.
  const handleToggleActive = async () => {
    if (!id || !projectId) return;
    try {
      setTogglingActive(true);
      const response = await lmsAPI.setJourneyActive(id, !savedActive, projectId);
      setSavedActive(!savedActive);
      setJourneyForm((form) => ({ ...form, is_active: !savedActive }));
      if (response.data?.warning) alert(response.data.warning);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to update journey status';
      console.error('Error toggling journey status:', err);
      alert(message);
    } finally {
      setTogglingActive(false);
    }
  };

  // What the notes beside the due fields need to know. Notes only: none of them
  // stops a save.
  const hasDueRule = journeyForm.due_within_days > 0 || stages.some((stage) => stage.due_within_days > 0);
  // Only a session the journey cannot be finished without: a required one that is still on.
  const hasSession = stages.some((stage) => stage.content.some(
    (item) => item.content_type === 'session' && item.is_required && item.is_available !== false,
  ));
  /**
   * The nearest earlier stage whose days are the same as or more than this one's.
   * Every stage counts from the same day, so "7, 7, 7" does not mean a week each:
   * it makes all three due on the same day.
   */
  const earlierStageDueNoSooner = (stageId: string): JourneyStage | undefined => {
    const index = stages.findIndex((stage) => stage.id === stageId);
    const own = index >= 0 ? stages[index].due_within_days : 0;
    if (own <= 0) return undefined;
    return stages.slice(0, index).reverse().find((stage) => stage.due_within_days > 0 && stage.due_within_days >= own);
  };

  const addStage = () => {
    const newStage: JourneyStage = {
      id: nextTempId(),
      title: `Stage ${stages.length + 1}`,
      description: '',
      order_index: stages.length,
      unlock_condition: 'sequential',
      required_score: 70,
      is_milestone: false,
      milestone_name: '',
      milestone_points_bonus: 0,
      due_within_days: 0,
      content: [],
    };
    setStages([...stages, newStage]);
    setExpandedStage(newStage.id);
  };

  const updateStage = (stageId: string, updates: Partial<JourneyStage>) => {
    setStages((prev) => prev.map((s) => (s.id === stageId ? { ...s, ...updates } : s)));
  };

  const removeStage = (stageId: string) => {
    const stage = stages.find((s) => s.id === stageId);
    const warning = stage && !isTempId(stage.id)
      ? 'Remove this stage? Its content and every learner\'s progress through it are deleted when you save.'
      : 'Remove this stage? This will also remove all content within it.';
    if (confirm(warning)) {
      setStages((prev) => prev.filter((s) => s.id !== stageId));
      if (expandedStage === stageId) setExpandedStage(null);
    }
  };

  // Stage order is the unlock order, so it has to be editable without a
  // drag-and-drop dependency.
  const moveStage = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= stages.length) return;
    const next = [...stages];
    [next[index], next[target]] = [next[target], next[index]];
    setStages(next.map((s, i) => ({ ...s, order_index: i })));
  };

  const addContentToStage = (stageId: string, contentType: 'module' | 'session') => {
    const stage = stages.find((s) => s.id === stageId);
    if (!stage) return;
    const newContent: StageContent = {
      id: nextTempId(),
      content_type: contentType,
      title: '',
      order_index: stage.content.length,
      is_required: true,
      is_available: true,
    };
    updateStage(stageId, { content: [...stage.content, newContent] });
  };

  const updateStageContent = (stageId: string, contentId: string, updates: Partial<StageContent>) => {
    const stage = stages.find((s) => s.id === stageId);
    if (!stage) return;
    updateStage(stageId, {
      content: stage.content.map((c) => (c.id === contentId ? { ...c, ...updates } : c)),
    });
  };

  const removeStageContent = (stageId: string, contentId: string) => {
    const stage = stages.find((s) => s.id === stageId);
    if (!stage) return;
    updateStage(stageId, {
      content: stage.content.filter((c) => c.id !== contentId).map((c, i) => ({ ...c, order_index: i })),
    });
  };

  const moveStageContent = (stageId: string, index: number, direction: -1 | 1) => {
    const stage = stages.find((s) => s.id === stageId);
    if (!stage) return;
    const target = index + direction;
    if (target < 0 || target >= stage.content.length) return;
    const next = [...stage.content];
    [next[index], next[target]] = [next[target], next[index]];
    updateStage(stageId, { content: next.map((c, i) => ({ ...c, order_index: i })) });
  };

  /**
   * Options for one content row: everything of that type except what the same
   * stage already uses, plus whatever this row currently holds. The same module
   * twice in one stage is rejected by a unique index server-side.
   */
  const optionsFor = (stage: JourneyStage, content: StageContent): PickerItem[] => {
    const pool = content.content_type === 'module' ? availableModules : availableSessions;
    const selectedId = content.content_type === 'module' ? content.module_id : content.session_id;
    const taken = new Set(
      stage.content
        .filter((c) => c.id !== content.id && c.content_type === content.content_type)
        .map((c) => (c.content_type === 'module' ? c.module_id : c.session_id))
        .filter((value): value is string => Boolean(value)),
    );
    return pool.filter((item) => !taken.has(item.id) || item.id === selectedId);
  };

  if (loading) {
    return <PageLoader />;
  }

  if (loadError) {
    return (
      <div className="rounded-lg border border-gray-100 bg-white p-12 text-center">
        <Flag className="w-10 h-10 mx-auto text-gray-300 mb-4" />
        <h2 className="text-lg font-medium text-gray-900 mb-1">{loadError}</h2>
        <button
          onClick={() => navigate('/lms/journeys')}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
        >
          Back to journeys
        </button>
      </div>
    );
  }

  const totalContent = stages.reduce((acc, s) => acc + s.content.length, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <LmsEditorHeader
        onBack={() => navigate('/lms/journeys')}
        title={isEditing ? 'Edit Journey' : 'Create Journey'}
        subtitle="Build a structured learning path with stages"
        actions={
          <div className="flex items-center gap-2">
            {isEditing && (
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                  savedActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {savedActive ? 'Active' : 'Inactive'}
              </span>
            )}
            {isEditing && id && (
              <button
                onClick={handleToggleActive}
                disabled={togglingActive}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {savedActive ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                {savedActive ? 'Deactivate' : 'Activate'}
              </button>
            )}
            {isEditing && id && (
              <button
                onClick={() => navigate(`/lms/journeys/${id}/assignments`)}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <UserPlus className="w-4 h-4" />
                Assign Learners
              </button>
            )}
            <FormActionButtons
              onCancel={() => navigate('/lms/journeys')}
              onSave={handleSave}
              saving={saving}
              saveLabel="Save Journey"
            />
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Journey Details */}
        <div className="lg:col-span-1 space-y-6">
          <div className="rounded-lg border border-gray-100 bg-white p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Journey Details</h2>

            <div className="space-y-4">
              <LmsTitleDescriptionFields
                title={journeyForm.title}
                onTitleChange={(value) => setJourneyForm({ ...journeyForm, title: value })}
                titlePlaceholder="Enter journey title"
                description={journeyForm.description}
                onDescriptionChange={(value) => setJourneyForm({ ...journeyForm, description: value })}
                descriptionPlaceholder="Describe this learning journey"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Thumbnail URL</label>
                <input
                  type="url"
                  value={journeyForm.thumbnail_url}
                  onChange={(e) => setJourneyForm({ ...journeyForm, thumbnail_url: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="https://…"
                />
                <p className="text-xs text-gray-500 mt-1">Shown on the journey card in the app</p>
                {journeyForm.thumbnail_url.trim() && (
                  <img
                    src={journeyForm.thumbnail_url}
                    alt=""
                    className="mt-2 h-24 w-full rounded-lg border border-gray-200 object-cover"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Hours</label>
                <input
                  type="number"
                  value={journeyForm.estimated_hours}
                  onChange={(e) =>
                    setJourneyForm({
                      ...journeyForm,
                      estimated_hours: Math.max(0, parseFloat(e.target.value) || 0),
                    })
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  min={0}
                  step={0.5}
                />
                <p className="text-xs text-gray-500 mt-1">Displayed to learners as the expected time commitment</p>
              </div>

              <div>
                <label htmlFor="journey-due-within-days" className="block text-sm font-medium text-gray-700 mb-1">
                  Complete within (days)
                </label>
                <input
                  id="journey-due-within-days"
                  type="number"
                  value={journeyForm.due_within_days || ''}
                  onChange={(e) => setJourneyForm({ ...journeyForm, due_within_days: toDueDays(e.target.value) })}
                  placeholder="No due date"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  min={0}
                  max={DUE_DAYS_MAX}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave blank for no due date. Each learner the journey is assigned to gets this many days from the
                  day it is assigned to them, or from their joining date if they join later. For a journey people
                  already have, the days count from the day this is switched on.
                </p>
                {/* Notes only: none of these stops the journey being saved. */}
                {hasDueRule && journeyForm.audience === 'everyone' && (
                  <p className="text-xs text-amber-700 mt-1 flex items-start gap-1">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>
                      Due dates and reminders go only to learners this journey is assigned to on Assign Learners.
                      Nobody else gets a due date.
                    </span>
                  </p>
                )}
                {hasDueRule && hasSession && (
                  <p className="text-xs text-amber-700 mt-1 flex items-start gap-1">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>
                      This journey includes a required live session. A learner who is given the journey after the
                      session has taken place cannot finish it, and will show as overdue.
                    </span>
                  </p>
                )}
              </div>

              <LmsAudiencePicker
                projectId={projectId}
                targetLabel="journey"
                audience={journeyForm.audience}
                onAudienceChange={(audience) => setJourneyForm({ ...journeyForm, audience })}
                roleIds={assignedRoleIds}
                onRoleIdsChange={setAssignedRoleIds}
                employeeCodes={assignedEmployeeCodes}
                onEmployeeCodesChange={setAssignedEmployeeCodes}
                disabled={saving}
              />

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={journeyForm.is_active}
                    onChange={(e) => setJourneyForm({ ...journeyForm, is_active: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
                <p className="text-xs text-gray-500 mt-1">Active journeys are visible to assigned trainees</p>
              </div>
            </div>
          </div>

          {/* Stats Preview */}
          <div className="rounded-lg border border-gray-100 bg-white p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Summary</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Total Stages</span>
                <span className="font-semibold text-gray-900">{stages.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Total Content</span>
                <span className="font-semibold text-gray-900">{totalContent}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Milestones</span>
                <span className="font-semibold text-gray-900">
                  {stages.filter((s) => s.is_milestone).length}
                </span>
              </div>
            </div>

            {stages.length > 0 && totalContent === 0 && (
              <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>This journey has no content yet. It cannot be activated until a stage has a module or session.</span>
              </div>
            )}
          </div>
        </div>

        {/* Stages Builder */}
        <div className="lg:col-span-2">
          <div className="rounded-lg border border-gray-100 bg-white p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Stages ({stages.length})</h2>
              <button
                onClick={addStage}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50"
              >
                <Plus className="w-4 h-4" />
                Add Stage
              </button>
            </div>

            {stages.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                <Flag className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">No stages yet</h3>
                <p className="text-gray-500 mb-4">Add stages to create your learning path</p>
                <button
                  onClick={addStage}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50"
                >
                  <Plus className="w-4 h-4" />
                  Add first stage
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {stages.map((stage, index) => {
                  const isExpanded = expandedStage === stage.id;

                  return (
                    <div key={stage.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Stage Header */}
                      <div
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer ${
                          stage.is_milestone ? 'bg-yellow-50' : 'bg-gray-50'
                        }`}
                        onClick={() => setExpandedStage(isExpanded ? null : stage.id)}
                      >
                        <div className="flex flex-col" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => moveStage(index, -1)}
                            disabled={index === 0}
                            title="Move stage up"
                            className="text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-400"
                          >
                            <ChevronUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => moveStage(index, 1)}
                            disabled={index === stages.length - 1}
                            title="Move stage down"
                            className="text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-400"
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                          stage.is_milestone
                            ? 'bg-yellow-200 text-yellow-800'
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          {index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{stage.title}</p>
                          <p className="text-xs text-gray-500">
                            {stage.content.length} items · {stage.unlock_condition === 'sequential'
                              ? 'Sequential unlock'
                              : `Needs ${stage.required_score}% in the previous stage`}
                            {stage.due_within_days > 0 && ` · due within ${daysLabel(stage.due_within_days)}`}
                          </p>
                        </div>
                        {stage.is_milestone && (
                          <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full flex items-center gap-1">
                            <Trophy className="w-3 h-3" />
                            Milestone
                          </span>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeStage(stage.id);
                          }}
                          className="p-1 text-gray-400 hover:text-red-500 rounded"
                          title="Remove stage"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                      </div>

                      {/* Stage Content */}
                      {isExpanded && (
                        <div className="p-4 space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Stage Title</label>
                              <input
                                type="text"
                                value={stage.title}
                                onChange={(e) => updateStage(stage.id, { title: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                placeholder="Enter stage title"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Unlock Condition</label>
                              <select
                                value={stage.unlock_condition}
                                onChange={(e) => updateStage(stage.id, { unlock_condition: e.target.value as UnlockCondition })}
                                disabled={index === 0}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
                              >
                                <option value="sequential">Complete previous stage</option>
                                <option value="score_based">Score-based</option>
                              </select>
                              {index === 0 && (
                                <p className="text-xs text-gray-500 mt-1">The first stage is always unlocked.</p>
                              )}
                            </div>
                          </div>

                          {stage.unlock_condition === 'score_based' && index > 0 && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Required Score (%)</label>
                              <input
                                type="number"
                                value={stage.required_score}
                                onChange={(e) =>
                                  updateStage(stage.id, {
                                    required_score: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)),
                                  })
                                }
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                min={0}
                                max={100}
                              />
                              <p className="text-xs text-amber-700 mt-1 flex items-start gap-1">
                                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                <span>
                                  Measured against the average quiz score of "{stages[index - 1]?.title}". Quizzes
                                  can only be attempted once, so a learner who scores below this stays locked out.
                                </span>
                              </p>
                            </div>
                          )}

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                              value={stage.description}
                              onChange={(e) => updateStage(stage.id, { description: e.target.value })}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                              rows={2}
                              placeholder="Describe this stage"
                            />
                          </div>

                          <div>
                            {/* Not the journey field's label: both are on screen together. */}
                            <label
                              htmlFor={`stage-due-within-days-${stage.id}`}
                              className="block text-sm font-medium text-gray-700 mb-1"
                            >
                              Finish this stage within (days)
                            </label>
                            <input
                              id={`stage-due-within-days-${stage.id}`}
                              type="number"
                              value={stage.due_within_days || ''}
                              onChange={(e) => updateStage(stage.id, { due_within_days: toDueDays(e.target.value) })}
                              placeholder="No stage due date"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              min={0}
                              max={DUE_DAYS_MAX}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Optional. Days are counted from the day the learner got the journey, not from when this
                              stage opens: for a week per stage, enter 7, 14, 21. For learners who already have the
                              journey, the days count from the day this stage's days are switched on. Leave blank and
                              only the journey's due date applies.
                            </p>
                            {/* Notes only: the journey can still be saved like this. */}
                            {journeyForm.due_within_days > 0 && stage.due_within_days > journeyForm.due_within_days && (
                              <p className="text-xs text-amber-700 mt-1 flex items-start gap-1">
                                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                <span>This is later than the journey's own {daysLabel(journeyForm.due_within_days)}.</span>
                              </p>
                            )}
                            {(() => {
                              const earlier = earlierStageDueNoSooner(stage.id);
                              return earlier ? (
                                <p className="text-xs text-amber-700 mt-1 flex items-start gap-1">
                                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                  <span>
                                    This is no later than "{earlier.title}", an earlier stage (
                                    {daysLabel(earlier.due_within_days)}). For anyone given the journey from now on, both
                                    count from the same day.
                                  </span>
                                </p>
                              ) : null;
                            })()}
                          </div>

                          {/* Milestone Settings */}
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={stage.is_milestone}
                                onChange={(e) => updateStage(stage.id, { is_milestone: e.target.checked })}
                                className="w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                              />
                              <span className="text-sm font-medium text-gray-700">Mark as Milestone</span>
                            </label>
                            {stage.is_milestone && (
                              <div className="mt-3 grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Milestone Name</label>
                                  <input
                                    type="text"
                                    value={stage.milestone_name}
                                    onChange={(e) => updateStage(stage.id, { milestone_name: e.target.value })}
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                    placeholder="e.g., Gold Badge"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Bonus Points</label>
                                  <input
                                    type="number"
                                    value={stage.milestone_points_bonus}
                                    onChange={(e) =>
                                      updateStage(stage.id, {
                                        milestone_points_bonus: Math.max(0, parseInt(e.target.value) || 0),
                                      })
                                    }
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                    min={0}
                                  />
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Stage Content */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-sm font-medium text-gray-700">Content</label>
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => addContentToStage(stage.id, 'module')}
                                  className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                                >
                                  <BookOpen className="w-3 h-3" />
                                  Add Module
                                </button>
                                <button
                                  onClick={() => addContentToStage(stage.id, 'session')}
                                  className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                                >
                                  <Calendar className="w-3 h-3" />
                                  Add Session
                                </button>
                              </div>
                            </div>

                            {catalogLoading && (
                              <p className="mb-2 text-xs text-gray-500 flex items-center gap-1">
                                <Spinner size="xs" />
                                Loading available modules and sessions…
                              </p>
                            )}
                            {!catalogLoading && availableModules.length === 0 && (
                              <p className="mb-2 text-xs text-amber-700">
                                No modules available — publish a content module and tick "Available in learning
                journeys" on it before adding it to a stage.
                              </p>
                            )}

                            {stage.content.length === 0 ? (
                              <div className="text-center py-6 border border-dashed border-gray-200 rounded-lg">
                                <p className="text-sm text-gray-500">No content added yet</p>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {stage.content.map((content, contentIndex) => {
                                  const selectedId =
                                    (content.content_type === 'module' ? content.module_id : content.session_id) || '';
                                  const options = optionsFor(stage, content);

                                  return (
                                    <div key={content.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                                      <div className="flex flex-col">
                                        <button
                                          onClick={() => moveStageContent(stage.id, contentIndex, -1)}
                                          disabled={contentIndex === 0}
                                          title="Move up"
                                          className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                                        >
                                          <ChevronUp className="w-3 h-3" />
                                        </button>
                                        <button
                                          onClick={() => moveStageContent(stage.id, contentIndex, 1)}
                                          disabled={contentIndex === stage.content.length - 1}
                                          title="Move down"
                                          className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                                        >
                                          <ChevronDown className="w-3 h-3" />
                                        </button>
                                      </div>
                                      <span className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
                                        content.content_type === 'module'
                                          ? 'bg-purple-100 text-purple-600'
                                          : 'bg-green-100 text-green-600'
                                      }`}>
                                        {content.content_type === 'module' ? (
                                          <BookOpen className="w-3.5 h-3.5" />
                                        ) : (
                                          <Calendar className="w-3.5 h-3.5" />
                                        )}
                                      </span>
                                      <div className="flex-1 min-w-0">
                                        <select
                                          value={selectedId}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            const item = options.find((i) => i.id === value);
                                            updateStageContent(stage.id, content.id, {
                                              module_id: content.content_type === 'module' ? value || undefined : undefined,
                                              session_id: content.content_type === 'session' ? value || undefined : undefined,
                                              title: item?.title || '',
                                              is_available: true,
                                            });
                                          }}
                                          className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                                            selectedId ? 'border-gray-300' : 'border-amber-400 bg-amber-50'
                                          }`}
                                        >
                                          <option value="">Select a {content.content_type}…</option>
                                          {/* An item that is no longer in the catalog (archived module,
                                              cancelled session) would otherwise render as a blank select
                                              and get silently reassigned on the next save. */}
                                          {selectedId && !options.some((o) => o.id === selectedId) && (
                                            <option value={selectedId}>{content.title || 'Unavailable content'}</option>
                                          )}
                                          {options.map((item) => (
                                            <option key={item.id} value={item.id}>
                                              {item.subtitle ? `${item.title} · ${item.subtitle}` : item.title}
                                            </option>
                                          ))}
                                        </select>
                                        {content.is_available === false && (
                                          <p className="text-xs text-amber-700 mt-0.5 flex items-center gap-1">
                                            <AlertTriangle className="w-3 h-3" />
                                            Not visible to learners (unpublished or cancelled)
                                          </p>
                                        )}
                                      </div>
                                      <label className="flex items-center gap-1 text-xs whitespace-nowrap" title="Required items must be completed before the stage unlocks the next one">
                                        <input
                                          type="checkbox"
                                          checked={content.is_required}
                                          onChange={(e) => updateStageContent(stage.id, content.id, { is_required: e.target.checked })}
                                          className="w-3 h-3"
                                        />
                                        Required
                                      </label>
                                      <button
                                        onClick={() => removeStageContent(stage.id, content.id)}
                                        className="p-1 text-gray-400 hover:text-red-500 rounded"
                                        title="Remove content"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
